// Э-12. Администрирование: пользователи и роли (у каждого диспетчера — личный логин и PIN),
// настройки расчёта и порогов, журнал действий.

import { useState } from "react";
import { AuditTable, type AuditRow } from "../../shared/audit";
import { TopBar } from "../../shared/shell";
import { Button, Drawer, Field, Input, Loading, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { api, ROLES, useCtx } from "../ctx";

const SETTINGS: { key: string; label: string; hint?: string; type?: "select"; options?: Record<string, string> }[] = [
  { key: "lateness_tolerance_minutes", label: "Допуск опоздания, мин" },
  { key: "lateness_reference", label: "Опоздание считать по", type: "select", options: { arrival: "прибытию на объект", departure: "выезду с завода" } },
  { key: "alert_no_vehicle_minutes", label: "Предупреждать, если машина не назначена за N мин до погрузки" },
  { key: "alert_load_not_started_minutes", label: "Предупреждать, если погрузка не начата через N мин после плана" },
  { key: "min_lead_days", label: "Заявка «срочная», если подана меньше чем за N дней" },
  { key: "search_step_minutes", label: "Шаг поиска окна, мин" },
  { key: "availability_options", label: "Сколько вариантов предлагать, если окно занято", hint: "Ближайшие раньше и позже, другая РБУ и свободные окна за сутки" },
  { key: "default_travel_minutes", label: "Время в пути по умолчанию, мин" },
  { key: "recurring_horizon_days", label: "Регулярные заявки создавать на N дней вперёд" },
  { key: "operator_shift_hours", label: "Длительность смены оператора, ч" },
  { key: "plant_days_off", label: "Нерабочие дни завода", hint: "Через запятую, ДД.ММ или диапазон ДД.ММ-ДД.ММ. Завод работает без выходных; план по дням эти даты пропускает" },
  { key: "timezone", label: "Часовой пояс по умолчанию" },
  // Этап 2: транспорт и водители
  { key: "dispatch_phone", label: "Телефон диспетчерской", hint: "Показывается водителям и клиентам на странице отслеживания" },
  { key: "geofence_auto_status", label: "Ставить статусы рейса по местоположению телефона", type: "select", options: { false: "нет — статусы отмечают люди", true: "да — по зонам вокруг завода и объекта" },
    hint: "У аэропорта местоположение бывает неточным — поэтому по умолчанию выключено; координаты всё равно сохраняются" },
  { key: "geofence_plant_m", label: "Зона завода, м", hint: "Ближе — «машина на заводе» и «вернулась»" },
  { key: "geofence_site_m", label: "Зона объекта, м", hint: "Ближе — «на объекте», дальше после разгрузки — «возврат»" },
  { key: "gps_max_accuracy_m", label: "Не учитывать координаты грубее, м" },
  { key: "gps_interval_seconds", label: "Координаты с телефона водителя — раз в N секунд" },
  { key: "site_idle_tolerance_minutes", label: "Допуск простоя на объекте сверх плановой разгрузки, мин" },
  { key: "alert_vehicle_return_minutes", label: "Предупреждать, если машина опаздывает к следующему рейсу на N мин" },
  { key: "track_keep_days", label: "Хранить треки машин, дней" },
  // Этап 3: склад и лаборатория
  { key: "strength_factor", label: "Требуемая прочность = класс × коэффициент", hint: "По ГОСТ 18105 обычно 1,28 (при неизвестной однородности); уточняет лаборатория" },
  { key: "stock_days_warn", label: "Предупреждать, если сырья хватит меньше чем на N дней" },
  { key: "stock_avg_days", label: "Средний расход считать за N дней" },
  { key: "tracking_link_days", label: "Ссылка клиента работает N дней после даты заявки" },
];

export function AdminPage() {
  const [tab, setTab] = useState<"users" | "settings" | "audit">("users");
  return (
    <>
      <TopBar title="Администрирование" />
      <div className="content">
        <Tabs value={tab} onChange={setTab} items={[{ value: "users", label: "Пользователи" }, { value: "settings", label: "Настройки" }, { value: "audit", label: "Журнал действий" }]} />
        {tab === "users" && <Users />}
        {tab === "settings" && <Settings />}
        {tab === "audit" && <Audit />}
      </div>
    </>
  );
}

function Users() {
  const { refs } = useCtx();
  const list = useAsync<any>(() => api.get("/users"), []);
  const [edit, setEdit] = useState<any | null>(null);
  return (
    <div className="stack">
      <div className="row"><div className="grow" /><Button variant="primary" onClick={() => setEdit({ role: "dispatcher", plant_ids: refs.plants.map((p) => p.id), is_active: true })}>Добавить пользователя</Button></div>
      {!list.data ? <Loading error={list.error} /> : (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr><th>ФИО</th><th>Логин</th><th>Роль</th><th>Заводы</th><th>PIN</th><th>Состояние</th></tr></thead>
            <tbody>{list.data.items.map((u: any) => (
              <tr key={u.id} className="click" onClick={() => setEdit(u)}>
                <td>{u.full_name}</td><td>{u.login}</td><td>{ROLES[u.role]}{u.is_senior ? ", старший" : ""}</td>
                <td className="small">{["admin", "director"].includes(u.role) ? "все" : u.plant_ids.map((id: string) => refs.plants.find((p) => p.id === id)?.name).join(", ")}</td>
                <td>{u.has_pin ? "задан" : "—"}</td>
                <td>{!u.is_active ? <span className="tag s-conflict">заблокирован</span> : u.locked ? <span className="tag s-loading">временная блокировка</span> : <span className="tag s-done">активен</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <p className="muted small">У каждого диспетчера — личный логин: иначе журнал действий и передача смены теряют смысл. Для быстрой смены за общим ПК задайте PIN.</p>
      {edit && <UserForm user={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); list.reload(); }} />}
    </div>
  );
}

function UserForm({ user, onClose, onSaved }: { user: any; onClose: () => void; onSaved: () => void }) {
  const { refs } = useCtx();
  const toast = useToast();
  const isNew = !user.id;
  const [v, setV] = useState({ login: user.login || "", full_name: user.full_name || "", phone: user.phone || "", role: user.role, password: "", pin: "", is_active: user.is_active ?? true, is_senior: !!user.is_senior });
  const [plants, setPlants] = useState<string[]>(user.plant_ids || []);
  return (
    <Drawer narrow title={isNew ? "Новый пользователь" : user.full_name} onClose={onClose} footer={<>
      <Button variant="primary" onClick={async () => {
        try {
          const body: any = { full_name: v.full_name, phone: v.phone || null, role: v.role, plant_ids: plants, is_active: v.is_active, is_senior: v.role === "dispatcher" && v.is_senior };
          if (v.password) body.password = v.password;
          if (v.pin) body.pin = v.pin;
          if (isNew) await api.post("/users", { ...body, login: v.login });
          else await api.patch(`/users/${user.id}`, body);
          toast.ok("Сохранено");
          onSaved();
        } catch (e) { toast.error(e); }
      }}>Сохранить</Button>
      <Button onClick={onClose}>Отмена</Button>
    </>}>
      <div className="form-grid">
        <Field label="ФИО" wide><Input value={v.full_name} onChange={(e) => setV({ ...v, full_name: e.target.value })} /></Field>
        <Field label="Логин"><Input value={v.login} disabled={!isNew} onChange={(e) => setV({ ...v, login: e.target.value })} /></Field>
        <Field label="Телефон"><Input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
        <Field label="Роль"><Select value={v.role} onChange={(e) => setV({ ...v, role: e.target.value })} options={Object.entries(ROLES).filter(([k]) => k !== "driver").map(([value, label]) => ({ value, label }))} /></Field>
        <Field label={isNew ? "Пароль" : "Новый пароль"} hint={isNew ? "Не короче 6 символов" : "Смена пароля завершает все сессии"}>
          <Input type="password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} />
        </Field>
        <Field label="PIN (4–6 цифр)" hint="Для входа за общим ПК"><Input inputMode="numeric" value={v.pin} onChange={(e) => setV({ ...v, pin: e.target.value.replace(/\D/g, "") })} /></Field>
        <Field label="Доступ" wide>
          <label className="check"><input type="checkbox" checked={v.is_active} onChange={(e) => setV({ ...v, is_active: e.target.checked })} />Активен</label>
          {v.role === "dispatcher" && <label className="check"><input type="checkbox" checked={v.is_senior} onChange={(e) => setV({ ...v, is_senior: e.target.checked })} />Старший диспетчер — видит историю смен</label>}
        </Field>
        <Field label="Заводы" wide hint="Пользователь видит только назначенные заводы; руководитель и администратор — все">
          <div className="row">{refs.plants.map((p) => (
            <label key={p.id} className="check"><input type="checkbox" checked={plants.includes(p.id)} onChange={(e) => setPlants(e.target.checked ? [...plants, p.id] : plants.filter((x) => x !== p.id))} />{p.name}</label>
          ))}</div>
        </Field>
      </div>
    </Drawer>
  );
}

function Settings() {
  const toast = useToast();
  const s = useAsync<any>(() => api.get("/settings"), []);
  const [v, setV] = useState<Record<string, any> | null>(null);
  if (!s.data) return <Loading error={s.error} />;
  const cur = v || s.data;
  return (
    <div className="panel pad stack" style={{ maxWidth: 760 }}>
      <div className="form-grid">
        {SETTINGS.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint} wide>
            {f.type === "select" ? (
              <Select value={String(cur[f.key])} onChange={(e) => setV({ ...cur, [f.key]: e.target.value })} options={Object.entries(f.options!).map(([value, label]) => ({ value, label }))} />
            ) : (
              <Input value={String(cur[f.key] ?? "")} onChange={(e) => setV({ ...cur, [f.key]: e.target.value })} />
            )}
          </Field>
        ))}
      </div>
      <div><Button variant="primary" disabled={!v} onClick={async () => {
        const values: Record<string, any> = {};
        for (const f of SETTINGS) values[f.key] = f.key === "geofence_auto_status" ? String(cur[f.key]) === "true"
          : ["timezone", "dispatch_phone"].includes(f.key) || f.type === "select" ? cur[f.key] : Number(String(cur[f.key]).replace(",", "."));
        try { s.set(await api.put("/settings", { values })); setV(null); toast.ok("Настройки сохранены"); } catch (e) { toast.error(e); }
      }}>Сохранить настройки</Button></div>
    </div>
  );
}

const AUDIT_SECTIONS: { value: string; label: string }[] = [
  { value: "Order", label: "Заявки" }, { value: "Trip", label: "Рейсы" }, { value: "Batch", label: "Замесы" },
  { value: "RecurringOrder", label: "Регулярные заявки" }, { value: "SupplyPlan", label: "План поставок" },
  { value: "Shift", label: "Смены" }, { value: "Task", label: "Задачи" }, { value: "Customer", label: "Клиенты" },
  { value: "ConstructionSite", label: "Объекты" }, { value: "Vehicle", label: "Машины" }, { value: "ConcreteGrade", label: "Марки" },
  { value: "MixingUnit", label: "РБУ" }, { value: "Material", label: "Сырьё" }, { value: "Receipt", label: "Приход сырья" },
  { value: "Inventory", label: "Инвентаризации" }, { value: "Recipe", label: "Рецептуры" }, { value: "ConcreteTest", label: "Испытания" },
  { value: "QualityPassport", label: "Паспорта качества" }, { value: "Setting", label: "Настройки" }, { value: "User", label: "Пользователи" },
];

function Audit() {
  const [f, setF] = useState({ entity: "", user_id: "", date_from: "", date_to: "" });
  const users = useAsync<any>(() => api.get("/users"), []);
  const a = useAsync<AuditRow[]>(() => api.get("/audit", { ...f, limit: 1000 }), [f.entity, f.user_id, f.date_from, f.date_to]);
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  return (
    <div className="stack">
      <div className="filters">
        <Field label="Раздел"><Select value={f.entity} onChange={(e) => set({ entity: e.target.value })} placeholder="все" options={AUDIT_SECTIONS} /></Field>
        <Field label="Сотрудник"><Select value={f.user_id} onChange={(e) => set({ user_id: e.target.value })} placeholder="все"
          options={(users.data?.items || []).map((u: any) => ({ value: u.id, label: u.full_name }))} /></Field>
        <Field label="С"><Input type="date" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} /></Field>
        <Field label="По"><Input type="date" value={f.date_to} onChange={(e) => set({ date_to: e.target.value })} /></Field>
      </div>
      {!a.data ? <Loading error={a.error} /> : a.data.length === 0 ? <p className="muted">Записей нет.</p> : (
        <div className="panel"><AuditTable rows={a.data} /></div>
      )}
      <p className="muted small">Журнал пишет каждое изменение заявок, рейсов, справочников и настроек: кто, когда, что было и что стало, причина. Пароли и PIN-коды не показываются.</p>
    </div>
  );
}
